import { Link, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { useHotelDashboard } from '@/hooks/useHotelDashboard'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { ChevronLeft, FileText, Printer } from 'lucide-react'

function fmtUsd(n: unknown): string {
  if (typeof n !== 'number' || Number.isNaN(n)) return '—'
  return `$${n.toLocaleString('en-US', { maximumFractionDigits: 0 })}`
}

function fmtPct(n: unknown): string {
  if (typeof n !== 'number' || Number.isNaN(n)) return '—'
  const sign = n > 0 ? '+' : ''
  return `${sign}${n.toFixed(1)}%`
}

export default function OwnerReport() {
  const { reportId } = useParams<{ reportId: string }>()
  const { hotelId } = useHotelDashboard()

  const { data: report, isLoading, error } = useQuery({
    queryKey: ['hotel_report', reportId, hotelId],
    queryFn: async () => {
      const { data, error: qErr } = await supabase
        .from('hotel_reports')
        .select('*')
        .eq('id', reportId!)
        .eq('hotel_id', hotelId!)
        .single()
      if (qErr) throw qErr
      return data
    },
    enabled: !!reportId && !!hotelId,
  })

  const summary = (report?.summary_json ?? {}) as Record<string, unknown>
  const kpi = summary.kpi_summary as Record<string, number | null> | undefined
  const vsPrior = summary.vs_prior_period as Record<string, number | null> | undefined
  const comp = summary.comp_performance as { latest_by_metric?: Record<string, unknown> } | null
  const events = summary.events as Array<Record<string, string>> | null
  const anomalies = summary.anomalies as Array<Record<string, unknown>> | undefined

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="h-64 animate-pulse rounded-xl bg-vesta-mist/50" />
      </div>
    )
  }

  if (error || !report) {
    return (
      <div className="p-6 text-center">
        <p className="text-vesta-navy/80">Report not found.</p>
        <Button asChild variant="outline" className="mt-4">
          <Link to="/reports">Back to Reports</Link>
        </Button>
      </div>
    )
  }

  return (
    <div className="min-h-full space-y-6 p-6 text-vesta-navy print:p-8">
      <div className="flex flex-wrap items-center justify-between gap-4 print:hidden">
        <Button asChild variant="outline" size="sm">
          <Link to="/reports">
            <ChevronLeft className="mr-1 h-4 w-4" />
            Reports
          </Link>
        </Button>
        <Button variant="outline" size="sm" onClick={() => window.print()}>
          <Printer className="mr-1 h-4 w-4" />
          Print / Save PDF
        </Button>
      </div>

      <header className="border-b border-vesta-navy/10 pb-6">
        <div className="flex flex-wrap items-center gap-2">
          <FileText className="h-5 w-5 text-vesta-gold" />
          <Badge variant="outline">
            {report.period_type === '7d'
              ? 'Weekly'
              : report.period_type === '30d'
                ? '1-Month'
                : '6-Month'}
          </Badge>
        </div>
        <h1 className="mt-2 text-2xl font-semibold text-vesta-navy">{report.title}</h1>
        <p className="mt-1 text-sm text-vesta-navy/70">
          {report.start_date} → {report.end_date} · Generated{' '}
          {new Date(report.generated_at).toLocaleString()}
        </p>
      </header>

      {report.ai_executive_summary && (
        <Card className="border border-vesta-navy/10 bg-white shadow-sm">
          <CardHeader>
            <CardTitle className="text-base">Executive summary</CardTitle>
          </CardHeader>
          <CardContent className="prose prose-sm max-w-none text-vesta-navy/90 whitespace-pre-wrap">
            {report.ai_executive_summary}
          </CardContent>
        </Card>
      )}

      {kpi && (
        <Card className="border border-vesta-navy/10 bg-white shadow-sm">
          <CardHeader>
            <CardTitle className="text-base">KPI summary</CardTitle>
            <p className="text-xs text-vesta-navy/65">{kpi.days_with_data} days with data</p>
          </CardHeader>
          <CardContent className="grid gap-3 sm:grid-cols-2">
            <p className="text-sm">Avg RevPAR: <strong>{fmtUsd(kpi.avg_revpar)}</strong></p>
            <p className="text-sm">Avg ADR: <strong>{fmtUsd(kpi.avg_adr)}</strong></p>
            <p className="text-sm">
              Avg Occupancy:{' '}
              <strong>
                {kpi.avg_occupancy != null ? `${(kpi.avg_occupancy * 100).toFixed(1)}%` : '—'}
              </strong>
            </p>
            <p className="text-sm">Total Revenue: <strong>{fmtUsd(kpi.total_revenue)}</strong></p>
            <p className="text-sm">Total GOP: <strong>{fmtUsd(kpi.total_gop)}</strong></p>
          </CardContent>
        </Card>
      )}

      {vsPrior && (
        <Card className="border border-vesta-navy/10 bg-white shadow-sm">
          <CardHeader>
            <CardTitle className="text-base">Vs prior period</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-2 text-sm sm:grid-cols-2">
            <p>RevPAR: {fmtPct(vsPrior.revpar_change_pct)}</p>
            <p>ADR: {fmtPct(vsPrior.adr_change_pct)}</p>
            <p>Occupancy: {fmtPct(vsPrior.occupancy_change_pct)}</p>
            <p>Revenue: {fmtPct(vsPrior.revenue_change_pct)}</p>
          </CardContent>
        </Card>
      )}

      {comp?.latest_by_metric && Object.keys(comp.latest_by_metric).length > 0 && (
        <Card className="border border-vesta-navy/10 bg-white shadow-sm">
          <CardHeader>
            <CardTitle className="text-base">Comp set performance</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            {Object.entries(comp.latest_by_metric).map(([metric, row]) => {
              const r = row as { subject_value?: number; comp_median?: number; rank?: number; comp_count?: number }
              return (
                <p key={metric}>
                  <span className="uppercase text-vesta-navy/65">{metric}</span>: {fmtUsd(r.subject_value)} vs median{' '}
                  {fmtUsd(r.comp_median)} — rank {r.rank} of {(r.comp_count ?? 0) + 1}
                </p>
              )
            })}
          </CardContent>
        </Card>
      )}

      {events && events.length > 0 && (
        <Card className="border border-vesta-navy/10 bg-white shadow-sm">
          <CardHeader>
            <CardTitle className="text-base">Events in period</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-sm">
              {events.map((e, i) => (
                <li key={i}>
                  <strong>{e.name}</strong> — {e.start_date}
                  {e.end_date && e.end_date !== e.start_date ? ` to ${e.end_date}` : ''} ({e.expected_impact})
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {anomalies && anomalies.length > 0 && (
        <Card className="border border-vesta-navy/10 bg-white shadow-sm">
          <CardHeader>
            <CardTitle className="text-base">Open anomalies</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-sm">
              {anomalies.map((a, i) => (
                <li key={i}>
                  {String(a.date)} — {String(a.metric)}: {String(a.description ?? '')}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {(summary.expense_highlights as Array<{ category: string; total: number }> | undefined)?.length ? (
        <Card className="border border-vesta-navy/10 bg-white shadow-sm">
          <CardHeader>
            <CardTitle className="text-base">Expense highlights</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            {(summary.expense_highlights as Array<{ category: string; total: number }>).map((e) => (
              <p key={e.category}>
                {e.category}: <strong>{fmtUsd(e.total)}</strong>
              </p>
            ))}
          </CardContent>
        </Card>
      ) : null}
    </div>
  )
}
